'use client';

import Image from 'next/image';
import Link from 'next/link';
import { useTranslation } from '@/context/LanguageContext';
import WaveDivider from '@/components/WaveDivider';
import DoubleWaveDivider from '@/components/DoubleWaveDivider';
import styles from './page.module.scss';

interface AboutContentProps {
  profileImage: string;
  profileImagePosition: {
    x: number;
    y: number;
  };
}

export default function AboutContent({ profileImage, profileImagePosition }: AboutContentProps) {
  const { t } = useTranslation();

  return (
    <>
      <section className={styles.hero}>
        <div className={styles.heroImageWrap}>
          <Image
            src="/media/DSCF0425_optimized.png"
            alt={t.about.heroAlt}
            fill
            priority
            style={{ objectFit: 'cover' }}
          />
          <div className={styles.heroOverlay} />
        </div>
        <div className={styles.heroContent}>
          <h1 className={styles.heroTitle}>
            {t.about.heroTitle.split('\n')[0]}<br />{t.about.heroTitle.split('\n')[1]}
          </h1>
          <p className={styles.heroSub}>
            {t.about.heroSubtitle}
          </p>
        </div>
      </section>

      <div className={styles.heroWave}>
        <DoubleWaveDivider />
      </div>

      <section className={`${styles.intro} section section--magenta`}>
        <div className="container">
          <div className={styles.introGrid}>
            <div className={styles.introText}>
              <h2 className={styles.sectionTitle}>{t.about.strandvaskerTitle}</h2>
              <p>
                {t.about.strandvaskerP1}
              </p>
              <p>
                {t.about.strandvaskerP2}
              </p>
            </div>
            <div className={styles.introImage}>
              <Image
                src={profileImage}
                alt={t.about.mortenAlt}
                width={600}
                height={600}
                style={{
                  objectFit: 'cover',
                  objectPosition: `${profileImagePosition.x}% ${profileImagePosition.y}%`,
                  width: '100%',
                  height: '100%',
                }}
              />
            </div>
          </div>
        </div>
      </section>

      <WaveDivider from="magenta" />

      <section className={`${styles.philosophy} section section--yellow`}>
        <div className="container">
          <div className={styles.philosophyGrid}>
            <div className={styles.philosophyVideo}>
              <video
                autoPlay
                muted
                loop
                playsInline
              >
                <source src="/media/KopOgVand_optimized.mp4" type="video/mp4" />
              </video>
            </div>
            <div className={styles.philosophyContent}>
              <h2 className={styles.sectionTitle}>{t.about.filosofiTitle}</h2>
              <blockquote className={styles.quote}>
                &ldquo;{t.about.filosofiQuote}&rdquo;
              </blockquote>
              <p>
                {t.about.filosofiP1}
              </p>
              <p>
                {t.about.filosofiP2}
              </p>
            </div>
          </div>
        </div>
      </section>

      <DoubleWaveDivider flip />

      <section className={styles.parallaxBreak} aria-label={t.about.imageAlt1} />

      <DoubleWaveDivider />

      <section className={`${styles.roastery} section section--magenta`}>
        <div className="container">
          <div className={styles.roasteryGrid}>
            <div className={styles.roasteryContent}>
              <h2 className={styles.sectionTitle}>{t.about.risterietTitle}</h2>
              <p>{t.about.risterietP1}</p>
              <p>{t.about.risterietP2}</p>
              <p>{t.about.risterietP3}</p>
            </div>
            <div className={styles.roasteryImage}>
              <Image
                src="/media/DSCF0403_optimized.png"
                alt={t.about.imageAlt1}
                fill
                sizes="(max-width: 768px) 100vw, 50vw"
                style={{ objectFit: 'cover' }}
              />
            </div>
          </div>
        </div>
      </section>

      <WaveDivider from="magenta" />

      <section className={`${styles.cta} section section--yellow`}>
        <div className="container">
          <div className={styles.ctaContent}>
            <h2 className={styles.ctaTitle}>{t.about.ctaTitle}</h2>
            <p className={styles.ctaSub}>
              {t.about.ctaBody}
            </p>
            <div className={styles.ctaButtons}>
              <Link href="/shop" className={styles.ctaBtn}>
                {t.about.ctaPrimary}
              </Link>
              <Link href="/abonnement" className={styles.ctaBtnOutline}>
                {t.about.ctaSecondary}
              </Link>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
